"use client";
import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
/* eslint-disable @next/next/no-img-element */
import { type FC, Fragment, type ReactNode, useState } from "react";
import { cn } from "@/utilities/tailwind";
import { Button } from "./ui/button";

type DeleteDialogProps = {
  title?: ReactNode;
  deleteFunction: () => Promise<void>;
  buttonElement?: {
    text: string;
    icon: ReactNode;
    styleClass: string;
  } | null;
  isLoading: boolean;
  afterFunction?: () => void;
  "data-delete-project-button"?: string;
  externalIsOpen?: boolean;
  externalSetIsOpen?: (isOpen: boolean) => void;
};

export const DeleteDialog: FC<DeleteDialogProps> = ({
  title = "Are you sure you want to delete?",
  deleteFunction,
  buttonElement = {
    icon: <PlusIcon className="h-4 w-4 text-primary-600" />,
    text: "Delete Project",
    styleClass: "",
  },
  isLoading,
  afterFunction,
  "data-delete-project-button": dataAttr,
  externalIsOpen,
  externalSetIsOpen,
}) => {
  const [isOpen, setIsOpen] = useState(externalIsOpen || false);

  function closeModal() {
    setIsOpen(false);
    externalSetIsOpen?.(false);
  }
  function openModal() {
    setIsOpen(true);
    externalSetIsOpen?.(true);
  }

  const handleFunction = async () => {
    try {
      await deleteFunction().then(() => {
        afterFunction?.();
      });
      closeModal();
    } catch (_error: any) {}
  };

  return (
    <>
      {buttonElement ? (
        <Button
          onClick={openModal}
          className={cn(
            "flex w-max h-max justify-center items-center gap-x-1 rounded-md px-3 py-2 text-sm font-semibold",
            buttonElement.styleClass
          )}
          data-delete-project-button={dataAttr}
          variant="ghost"
        >
          {buttonElement.icon}
          {buttonElement.text}
        </Button>
      ) : null}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl dark:bg-zinc-800 bg-white p-6 text-left align-middle  transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-medium leading-6 text-gray-900 dark:text-zinc-100"
                  >
                    {title}
                  </Dialog.Title>
                  <div className="flex flex-row gap-4 mt-10 justify-end">
                    <Button
                      className="text-zinc-900 text-lg bg-transparent border-black border dark:text-zinc-100 dark:border-zinc-100 hover:bg-transparent dark:hover:bg-zinc-900 dark:hover:text-white disabled:hover:bg-transparent disabled:hover:text-zinc-900"
                      onClick={closeModal}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="text-white text-lg bg-red-600 border-black  hover:bg-red-600 hover:text-white"
                      onClick={handleFunction}
                      disabled={isLoading}
                      isLoading={isLoading}
                    >
                      Continue
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
